import {A, E} from './util.mjs'
import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as c from './conf.mjs'
import * as eb from './elem-btn.mjs'
import * as em from './elem-misc.mjs'
import * as s from './views/svg.mjs'
import * as u from './util.mjs'

export function GalleryThumbs(ctx, entries) {
  const galleryId = u.makeGalleryId(ctx.seq.nextId())

  return a.map(
    entries,
    entry => GalleryThumb(entry, galleryId),
  )
}

function GalleryThumb(entry, galleryId) {
  a.reqInst(entry, u.GalleryEntry)
  a.reqValidStr(galleryId)

  return entry.valid() && E(`div`, {class: `gallery-thumb`},
    new GalleryThumbImg().init(entry, galleryId),
  )
}

/*
Implementation note. From HTML semantics perspective, it would be much nicer to
implement this as "img". However, the intrinsic content size of "img" makes it
harder to implement the fullscreen gallery in a responsive way that supports
different viewport proportions. Note that while we could use a "span" with an
absolutely-positioned "img", that would involve a wrapper element and create
z-index conflicts, both inside a modal and outside a modal.
*/
class GalleryMain extends u.Elem {
  static {dr.reg(this)}

  init(entry) {
    return E(
      this,
      A.cls(`gallery-main`).bgUrl(entry.getSrc(c.IMAGE_WIDTH_GIANT)),
      E(`span`, A.cls(`gallery-loading`)),
    )
  }
}

function GalleryControls(entries) {
  if (!u.isLong(entries)) return undefined

  return E(`div`, A.cls(`row-bet-cen gap-sm w-full`),
    GalleryBtnPrev.init(),
    E(`div`, A.cls(`gallery-frames`), GalleryFrames(entries)),
    GalleryBtnNext.init(),
  )
}

class GalleryBtn extends eb.Btn {
  gallery() {return this.anc(GalleryCls)}
  get A() {return A.button().cls(`gallery-btn`)}
  init() {return E(this, this.A)}
}

class GalleryBtnPrev extends GalleryBtn {
  static {dr.reg(this)}
  get A() {return super.A.cls(`--prev`)}
  init() {return E(super.init(), {}, s.ArrowLeft())}
  perform() {this.gallery().cyclerPrev()}
}

class GalleryBtnNext extends GalleryBtn {
  static {dr.reg(this)}
  get A() {return super.A.cls(`--next`)}
  init() {return E(super.init(), {}, s.ArrowRight())}
  perform() {this.gallery().cyclerNext()}
}

function GalleryFrames(entries) {
  return a.map(entries, entry => GalleryFrameImg.init(entry))
}

class GalleryCls extends u.MixCycler(u.Elem) {
  static {dr.reg(this)}

  get CyclerChild() {return GalleryFrameImg}

  init({id, entries, entry, hotkeys}) {
    a.reqValidStr(id)
    a.optInst(entry, u.GalleryEntry)

    if (a.isEmpty(entries)) return undefined
    const entryId = a.pkOpt(entry)

    return E(
      this,
      A.id(id).cls(`gallery`).dataProps({entryId, hotkeys}),
      new GalleryMain(this),
      GalleryControls(entries),
    ).initEntry(entry)
  }

  initEntry(entry) {
    this.desc(GalleryMain)?.init(entry)
    for (const val of this.descs(GalleryFrameImg)) val.setCurrent(a.pk(entry))
    return this
  }

  static async reinit(id, entry) {
    a.reqInst(entry, u.GalleryEntry)
    const tar = u.elemById(id)
    if (tar) tar.set(entry)
    else await this.open(id, entry)
  }

  static async open(id, entry) {
    a.reqValidStr(id)

    const entries = this.getEntries(id)
    entry = a.optInst(entry, u.GalleryEntry) || a.head(entries)

    const tar = new this().init({id, entries, entry, hotkeys: true})
    if (!tar) return

    const md = await u.imp(import(`./views/modals.mjs`))
    md.ModalFullscreen({autofocus: false}, tar).show()
  }

  // Note: the result may be a mixture of images and non-images. Some elements
  // may store gallery id in their dataset without actually being a gallery
  // thumbnail image. See `Msgs`.
  static getRefs(id) {
    return a.vac(id) && u.nodesByAttr(document, `data-gallery-id`, id)
  }

  static getThumbs(id) {return u.filterInsts(this.getRefs(id), GalleryThumbImg)}
  static getEntries(id) {return a.mapCompact(this.getThumbs(id), toEntry)}
  getEntries() {return this.constructor.getEntries(this.id)}
  getProps() {return {...super.getProps(), id: this.id}}

  setEntry(entry) {
    // TODO: why was this written?
    // const entries = this.getEntries()
    // if (a.isEmpty(entries)) return this.remove()

    this.initEntry(entry)
    this.cyclerShowCurrent()
    return this
  }

  cyclerTransition(_prev, next) {if (next) this.setEntry(next.entry())}

  connectedCallback() {
    if (this.getProps().hotkeys) {
      this.unlisten?.()
      this.unlisten = this.env.events.addEvent(`keydown`, this)
    }
  }

  disconnectedCallback() {
    this.unlisten?.()
    this.unlisten = undefined
  }

  handleEvent(eve) {
    if (eve.type === `keydown`) {
      switch (eve.key) {
        case c.KEY_ARROW_UP: return a.eventKill(eve), this.cyclerPrev()
        case c.KEY_ARROW_LEFT: return a.eventKill(eve), this.cyclerPrev()
        case c.KEY_ARROW_DOWN: return a.eventKill(eve), this.cyclerNext()
        case c.KEY_ARROW_RIGHT: return a.eventKill(eve), this.cyclerNext()
      }
    }
    return undefined
  }
}

class GalleryImg extends u.MixCyclerChild(em.MixExecableFakeBtn(u.MixElem(dg.global.HTMLImageElement))) {
  entry() {return new u.GalleryEntry(this.getProps())}
  entryId() {return this.getProps()?.id}

  init(entry) {
    entry = a.toInst(entry, u.GalleryEntry)

    return E(
      this,
      A.src(entry.getSrc(c.IMAGE_WIDTH_SMALL)).fakeBtn(),
    ).setProps(entry)
  }
}

function toEntry(val) {return val.entry()?.valid()}

class GalleryThumbImg extends GalleryImg {
  static customName = `gallery-thumb-img`
  static {dr.reg(this)}

  init(entry, id) {
    this.dataset.galleryId = a.reqPk(id)
    super.init(entry)
    return E(this, A.cls(`img-cov img-top-cen full-sz`))
  }

  galleryId() {return this.dataset.galleryId}
  async perform() {await GalleryCls.reinit(this.galleryId(), this.entry())}
}

class GalleryFrameImg extends GalleryImg {
  static customName = `gallery-frame-img`
  static {dr.reg(this)}
  init(entry) {return E(super.init(entry), A.cls(`gallery-frame`))}
  setCurrent(id) {return E(this, A.ariaCur(u.teq(this.entryId(), id)))}
  async perform() {this.anc(GalleryCls)?.setEntry(this.entry())}
}
